import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import navigate from 'libs/router/navigate';

import ActionButton from 'components/buttons/action-button/ActionButton';
import CampaignKpiSetting from '../campaign-kpi-setting/CampaignKpiSetting';
import { withModal } from 'components/modal/ModalHOC';
import PageHeading from 'components/page/page-heading/PageHeading';
import Spinner from 'components/spinner/Spinner';

import CampaignBudgetWidget from '../campaign-budget-widget/CampaignBudgetWidget';
import CampaignImpressionsWidget from '../campaign-impressions-widget/CampaignImpressionsWidget';
import CampaignReachWidget from '../campaign-reach-widget/CampaignReachWidget';
import CreatorFollowerProgressWidget from '../creator-follower-progress-widget/CreatorFollowerProgressWidget';
import CreatorPerformanceWidget from '../creator-perfomance-widget/CreatorPerformanceWidget';
import TaskProgressWidget from '../task-progress-widget/TaskProgressWidget';
import ContentPerformance from '../content-performance/ContentPerformance';
import ZalandoOutfitsProgressWidget from '../zalando-outfits-progress-widget/ZalandoOutfitsProgressWidget';

import { fetchKpiSettings, fetchSummary, resetSummary, saveKpiSettings } from '../../reducers/report-summary';
import { fetchMetaInfo } from 'modules/reporting/reducers/meta-info';

import { mapInfluencersById } from 'selectors/reporting';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerRealm } from 'utils/account';
import {
  getAvailableChannels,
  getSupportedTaskTypes,
  isZalandoOutfitOnlyCampaign
} from 'utils/reporting';
import {
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';

applyTranslations(require('./campaign-report-summary.i18n.json'));

import './campaign-report-summary.scss';

import type { Campaign } from 'types';
import type {
  KpiSettingValues,
  ReportingDeliverableTypes,
  ReportingMetaInfoIGStorySnippetType,
  ReportingSummary,
  ReportingSummaryInfluencersMap,
  ReportingType
} from 'modules/reporting/types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

type StateProps = {
  campaign: Campaign,
  reporting: ReportingSummary,
  isLoading: boolean,
  kpiSettingValue: KpiSettingValues,
  hasDefinedAttribution: boolean,
  hasAgreedOffers: boolean,
  isOutfitOnlyCampaign: boolean,
  igStorySnippetType: ReportingMetaInfoIGStorySnippetType,
  influencersMapById: ReportingSummaryInfluencersMap,
  availableTaskTypes: Array<ReportingDeliverableTypes>,
  isV2Campaign: boolean,
  availableTypes: Array<ReportingType>,
  consents: ConsentReducerConsentsMap,
  isSCP: boolean
};

type DispatchProps = {
  dispatchFetchSummary: (campaignId: number) => void,
  dispatchResetSummary: () => void,
  dispatchFetchMetaInfo: (a: number) => void,
  dispatchFetchKpiSettings: (campaignId: number) => void,
  dispatchSaveKpiSettings: (
    campaignId: number,
    kpiSettingValue: KpiSettingValues,
    saveSettingForUpcomingCamapign: boolean
  ) => void
};

type Props = StateProps & DispatchProps;

const CustomizeKpiSettingBtn = withModal(
  <ActionButton
    color="tertiary"
  >
    {__t('campaign_report_summary.customize_kpi_button')}
  </ActionButton>, {
    modalContent: CampaignKpiSetting,
    width: 'medium',
    positionOverride: 'fixed-to-top'
  });

const CampaignReportSummary = ({
  campaign,
  reporting,
  isLoading,
  availableTaskTypes,
  hasDefinedAttribution,
  hasAgreedOffers,
  igStorySnippetType,
  influencersMapById,
  isV2Campaign,
  kpiSettingValue,
  availableTypes,
  consents,
  isSCP,
  isOutfitOnlyCampaign,
  dispatchFetchSummary,
  dispatchResetSummary,
  dispatchFetchMetaInfo,
  dispatchSaveKpiSettings,
  dispatchFetchKpiSettings
}: Props) => {
  const campaignId = campaign?.id;

  React.useEffect(() => {
    if (campaignId) {
      dispatchFetchSummary(campaignId);
      dispatchFetchKpiSettings(campaignId);
    }

    return dispatchResetSummary;
  }, [campaignId]);

  const handleSaveKpiSetting = (
    kpiSetting: KpiSettingValues,
    saveSettingForUpcomingCamapign: boolean
  ) => {
    dispatchSaveKpiSettings(campaignId, kpiSetting, saveSettingForUpcomingCamapign);
    dispatchFetchMetaInfo(Number(campaign.id));
    dispatchFetchSummary(campaignId);
  };

  const emptyMessageKey = !hasAgreedOffers ? 'no_agreed_offers_message' : 'no_data_error';

  const handleNoPostClick = (): void => {
    navigate.to(`/campaigns/${campaign.id}/report/v2/content-metrics`);
  };

  const renderTitle = () => (
    <PageHeading
      heading={__t('campaign_report_summary.page_heading')}
      color="light"
      size="h1"
      margin="none"
    />
  );

  if (isLoading && !hasAgreedOffers) {
    return (
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    );
  }

  if (!isLoading && (isEmpty(reporting) || !isV2Campaign || !hasAgreedOffers)) {
    return (
      <div className="campaign-report-summary">
        {renderTitle()}
        <section className="campaign-report-summary__empty-message">
          {__t(`campaign_report_summary.${!isV2Campaign ? 'channel_not_supported_message' : emptyMessageKey}`)}
        </section>
      </div>
    );
  }

  const renderKPIs = () => {
    if (
      !availableTaskTypes.includes(DELIVERABLE_TYPE_ZALANDO_OUTFIT)
        || isSCP === false
    ) {
      return (
        <div className="campaign-report-summary__kpis">
          <CreatorFollowerProgressWidget
            creatorsProgress={reporting.creatorsProgress || {}}
            followersProgress={reporting.followersProgress || {}}
            totalCreators={reporting.totalCreators || 0}
            isLoading={isLoading}
            campaignDeliverableTypes={availableTaskTypes}
            availableTypes={availableTypes}
          />
          <TaskProgressWidget
            tasksProgress={{
              ...(reporting.tasksProgress || {}),
              zalandoOutfit: null
            }}
            availableTaskTypes={availableTaskTypes}
            isLoading={isLoading}
          />
        </div>
      );
    }

    const leftClasses = classnames('campaign-report-summary__kpis-left', {
      'campaign-report-summary__kpis-left--outfit-only': isOutfitOnlyCampaign
    });

    return (
      <div className="campaign-report-summary__kpis">
        <div className={leftClasses}>
          <TaskProgressWidget
            tasksProgress={reporting.tasksProgress || {}}
            availableTaskTypes={availableTaskTypes}
            isLoading={isLoading}
          />
          {isOutfitOnlyCampaign ? null : (
            <CreatorFollowerProgressWidget
              creatorsProgress={reporting.creatorsProgress || {}}
              followersProgress={reporting.followersProgress || {}}
              totalCreators={reporting.totalCreators || 0}
              isLoading={isLoading}
              campaignDeliverableTypes={availableTaskTypes}
              availableTypes={availableTypes}
            />
          )}
        </div>
        <div className="campaign-report-summary__kpis-right">
          <ZalandoOutfitsProgressWidget
            isLoading={isLoading}
            count={reporting?.outfitsProgress?.counts ?? {}}
            tasks={reporting?.outfitsProgress?.progress ?? null}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="campaign-report-summary">
      <div className="campaign-report-summary__heading">
        {renderTitle()}
        {
          availableTaskTypes.includes(DELIVERABLE_TYPE_INSTAGRAM_STORY) && (
            <CustomizeKpiSettingBtn
              modalContentProps={{
                kpiSettingValue,
                onSavingKpiSetting: handleSaveKpiSetting
              }}
            />
          )
        }
      </div>
      {renderKPIs()}
      {isOutfitOnlyCampaign ? null : (
        <CampaignBudgetWidget
          campaignId={campaignId}
          isOutdated={!hasDefinedAttribution}
          hasAgreedOffers={hasAgreedOffers}
          totalBudget={reporting.totalBudget}
          budget={reporting.budget}
          roi={reporting.roi}
          isLoading={isLoading}
        />
      )}
      {isOutfitOnlyCampaign ? null : (
        <CampaignReachWidget
          totalReach={reporting.totalReach}
          reach={reporting.reach || {}}
          isLoading={isLoading}
          availableTaskTypes={availableTaskTypes}
        />
      )}
      {isOutfitOnlyCampaign ? null : (
        <CampaignImpressionsWidget
          totalImpressions={reporting.totalImpressions}
          impressions={reporting.impressions}
          mediaValue={reporting.mediaValue}
          isLoading={isLoading}
          availableTaskTypes={availableTaskTypes}
        />
      )}
      {isOutfitOnlyCampaign ? null : (
        <ContentPerformance
          contentPerformance={reporting.contentPerformance}
          dataTypesToShow={availableTaskTypes}
          igStorySnippetType={igStorySnippetType}
          campaignId={campaign.id}
          isLoading={isLoading}
          consents={consents}
        />
      )}
      {isOutfitOnlyCampaign ? null : (
        <CreatorPerformanceWidget
          isLoading={isLoading}
          postsPerformance={reporting.postsPerformance}
          influencersMapById={influencersMapById}
          igStorySnippetType={igStorySnippetType}
          dataTypesToShow={availableTaskTypes}
          onNoPostClick={handleNoPostClick}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSummary: (campaignId: number) => dispatch(fetchSummary(campaignId)),
  dispatchResetSummary: () => dispatch(resetSummary()),
  dispatchFetchKpiSettings: (campaignId: number) => dispatch(fetchKpiSettings(campaignId)),
  dispatchFetchMetaInfo: (campaignId: number) => dispatch(fetchMetaInfo(campaignId)),
  dispatchSaveKpiSettings: (
    campaignId: number,
    kpiSettingValue: KpiSettingValues,
    saveSettingForUpcomingCamapign: boolean
  ) => dispatch(saveKpiSettings(campaignId, kpiSettingValue, saveSettingForUpcomingCamapign))
});

const mapStateToProps = (state): StateProps => {
  const {
    account,
    campaignBrief,
    reportingMetaInfo,
    reportingV2,
    campaignDeliverables,
    consentReducer,
    runtimeConfig
  } = state;
  const { zalandoOutfitTaskTypeEnabled } = runtimeConfig.config;
  const isSCP = isInfluencerRealm(account.roles) && zalandoOutfitTaskTypeEnabled;

  return ({
    consents: consentReducer.consents,
    campaign: campaignBrief.brief,
    isLoading: reportingV2.isLoading || reportingMetaInfo.metaInfo.isLoading,
    reporting: reportingV2.summary,
    influencersMapById: mapInfluencersById(state),
    isOutfitOnlyCampaign: isZalandoOutfitOnlyCampaign(
      reportingMetaInfo?.metaInfo?.campaignDeliverableTypes ?? []
    ),
    hasDefinedAttribution: reportingMetaInfo.metaInfo.hasDefinedAttribution,
    hasAgreedOffers: reportingMetaInfo.metaInfo.hasAgreedOffers,
    igStorySnippetType: reportingMetaInfo.metaInfo.instagramStorySnippetsType,
    kpiSettingValue: state.reportingV2.kpiSettingValue,
    availableTaskTypes: getSupportedTaskTypes(isSCP)
      .filter(type => reportingMetaInfo.metaInfo.campaignDeliverableTypes.includes(type)),
    isV2Campaign: campaignDeliverables.isV2Campaign,
    availableTypes: getAvailableChannels(reportingMetaInfo?.metaInfo?.campaignDeliverableTypes || []),
    isSCP
  });
};

export default connect(mapStateToProps, mapDispatchToProps)(CampaignReportSummary);
